import Honeybadger from 'honeybadger-js';
import React, { useMemo } from 'react';

import { useSendAddressCollectionSmsMutation } from '../../../generated/graphql';
import { getChangedValues } from '../../../helpers/getChangedValues';
import { findHouseholdsforAddrsCollection } from '../../../helpers/households';
import { editAddressCollectionSmsPath, editAddressCollectionSmsRoute } from '../../../helpers/routes';
import AddressHouseholdRail from '../../SharePage/AddressHouseholdRail';
import SmsSequence, { ShareValues } from '../../SharePage/SmsSequence';
import useShareTracking from '../../SharePage/useShareTracking';
import { useFilteredHouseholds } from '../GuestFilters/useFilteredHouseholds';
import { useSequenceState } from './useSequenceState';

const AddressCollectionSMS: React.FC = () => {
  const [sendSms, { loading: sendingMessage }] = useSendAddressCollectionSmsMutation();
  const {
    sequence,
    wwsLoading,
    guestIds,
    setGuestIds,
    setHideRecaptcha,
    hideRecaptcha,
    dispatch,
    recaptchaRef,
    seqId,
    showError,
    errorMessage,
    showSuccess,
    householdLoading,
    wwsData,
    defaultMessage,
    greeting,
    optout,
  } = useSequenceState();
  const { filteredHouseholds, setFilterCriteria } = useFilteredHouseholds();
  const households = useMemo(() => findHouseholdsforAddrsCollection('phone', filteredHouseholds), [filteredHouseholds]);

  const track = useShareTracking();

  const smsLink = `https://${process.env.REACT_APP_HOSTNAME}ac/${wwsData?.weddingWebsite.vanityUrl}`;

  const sendMessage = async (values: ShareValues) => {
    if (wwsLoading || sendingMessage) {
      return null;
    }

    const edited = getChangedValues(initialValues, values);

    track('Website Shared', {
      guestMessagedCount: guestIds.length,
      message: values.message,
      messageCustomized: Boolean(Object.keys(edited).length),
      messageType: sequence?.messagingSequence?.displayName,
      medium: 'sms',
    });

    dispatch({ type: 'RESET', payload: { hideRecaptcha: true, showSuccess: false, showError: false } });

    try {
      await sendSms({
        variables: {
          args: {
            body: '',
            guestIds,
            token: recaptchaRef.current?.getValue() || '',
          },
        },
      });
      dispatch({ type: 'SUCCESS' });
    } catch (error) {
      Honeybadger.notify(error, 'Error sending address collection sms', { context: { error } });
      dispatch({ type: 'ERROR', payload: { message: error.message } });
    } finally {
      recaptchaRef.current?.reset();
    }
  };

  let initialValues: ShareValues = {
    greeting,
    message: defaultMessage,
    optout,
  };

  return (
    <SmsSequence
      onSend={sendMessage}
      loading={sendingMessage}
      setGuestIds={setGuestIds}
      showError={showError}
      errorMessage={errorMessage}
      showSuccess={showSuccess}
      initialValues={initialValues}
      hideRecaptcha={hideRecaptcha}
      setHideRecaptcha={setHideRecaptcha}
      recaptchaRef={recaptchaRef}
      editPath={editAddressCollectionSmsPath}
      editRoute={editAddressCollectionSmsRoute(seqId)}
      households={households}
      wwsLoading={wwsLoading}
      householdLoading={householdLoading}
      HouseholdRailComponent={AddressHouseholdRail}
      smsLink={smsLink}
      displayName={sequence?.messagingSequence?.displayName}
      setFilter={setFilterCriteria}
    />
  );
};

export default AddressCollectionSMS;
